import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cdn } from 'constants/common';
import ErrorMessage from 'components/common/ErrorMessage';
import toast from 'react-hot-toast';
import Api from 'services/api';
import Swal from 'sweetalert2';
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';

const attachmentTypes = [
    'Identity Verification',
    'Certifications',
    'Evaluations',
    'Temple Recommend Verification',
    'Other',
];

const fileTypeLoadOptions = async () => {
    return {
        options: attachmentTypes.map((type) => ({ label: type, value: type })),
        hasMore: false,
    };
};

const documentUrl = (filePath) => `${cdn}${filePath?.startsWith('storage/') ? filePath : `storage/${filePath}`}`;

const VerificationDocuments = ({
    item,
    canManageDocuments = () => true,
    submitMode = 'immediate',
    control: parentControl,
    errors: parentErrors,
}) => {
    const { t } = useTranslation();
    const isDeferred = submitMode === 'deferred';
    const {
        control: agreementControl,
        handleSubmit: handleAgreementFormSubmit,
        reset: resetAgreementForm,
        formState: { errors: agreementErrors },
    } = useForm({
        defaultValues: {
            vendor_agreement_file: null,
            vendor_agreement_expiry: item?.vendor_agreement_expiry || '',
        },
    });
    const deferredAgreementControl = parentControl || agreementControl;
    const deferredAgreementErrors = parentErrors || agreementErrors;
    const {
        control: attachmentControl,
        handleSubmit: handleAttachmentFormSubmit,
        reset: resetAttachmentFields,
        formState: { errors: attachmentErrors },
    } = useForm({
        defaultValues: {
            attachment_name: '',
            file_type: attachmentTypes[0],
            file: null,
            expiration_date: '',
        },
    });
    const { fields: pendingAttachments, append, remove, update } = useFieldArray({
        control: parentControl || attachmentControl,
        name: 'attachments',
    });
    const [attachments, setAttachments] = useState(item?.attachments || []);
    const [showAttachmentForm, setShowAttachmentForm] = useState(false);
    const [editingAttachment, setEditingAttachment] = useState(null);

    useEffect(() => {
        resetAgreementForm({
            vendor_agreement_file: null,
            vendor_agreement_expiry: item?.vendor_agreement_expiry || '',
        });
        setAttachments(item?.attachments || []);
    }, [item, resetAgreementForm]);

    const refreshAttachments = async () => {
        const response = await Api.getInterpreterAttachments(item?.id);
        if (response?.status >= 200 && response?.status < 300) {
            setAttachments(response.data?.data || []);
        }
    };

    const handleAgreementSubmit = async (data) => {
        const formData = new FormData();
        if (data.vendor_agreement_file) formData.append('vendor_agreement_file', data.vendor_agreement_file);
        formData.append('vendor_agreement_expiry', data.vendor_agreement_expiry || '');

        const response = await Api.updateVendorAgreement(item.id, formData);
        if (response?.status >= 200 && response?.status < 300) {
            toast.success(response.data?.message || t('interpreter.vendorAgreementUpdated'), { className: 'successToast' });
            resetAgreementForm({
                vendor_agreement_file: null,
                vendor_agreement_expiry: data.vendor_agreement_expiry || '',
            });
        } else {
            toast.error(response?.data?.message || t('interpreter.vendorAgreementUpdateFailed'), { className: 'errorToast' });
        }
    };

    const resetAttachmentForm = () => {
        resetAttachmentFields({
            attachment_name: '',
            file_type: attachmentTypes[0],
            file: null,
            expiration_date: '',
        });
        setEditingAttachment(null);
        setShowAttachmentForm(false);
    };

    const [pendingAttachmentForm, setPendingAttachmentForm] = useState(null);
    const [deferredAttachmentErrors, setDeferredAttachmentErrors] = useState({});
    const [fileTypeState, setFileTypeState] = useState(null);

    const handleAttachmentSubmit = async (data) => {
        const formData = new FormData();
        formData.append('attachment_name', data.attachment_name);
        formData.append('file_type', data.file_type);
        formData.append('expiration_date', data.expiration_date || '');
        if (data.file) formData.append('file', data.file);

        const response = editingAttachment
            ? await Api.updateInterpreterAttachment(editingAttachment.id, formData)
            : await Api.addInterpreterAttachment(item.id, formData);

        if (response?.status >= 200 && response?.status < 300) {
            toast.success(response.data?.message || t('interpreter.attachmentSaved'), { className: 'successToast' });
            await refreshAttachments();
            resetAttachmentForm();
        } else {
            toast.error(response?.data?.message || t('interpreter.attachmentSaveFailed'), { className: 'errorToast' });
        }
    };

    const editAttachment = (attachment) => {
        if (isDeferred) {
            if (attachment._pendingIndex === undefined) {
                return;
            }
            setDeferredAttachmentErrors({});
            setEditingAttachment(attachment);
            setFileTypeState(attachment.file_type ? { label: attachment.file_type, value: attachment.file_type } : null);
            setPendingAttachmentForm({
                attachment_name: attachment.attachment_name || '',
                file_type: attachment.file_type || attachmentTypes[0],
                file: attachment.file || null,
                expiration_date: attachment.expiration_date || '',
            });
            setShowAttachmentForm(true);
            return;
        }
        setEditingAttachment(attachment);
        resetAttachmentFields({
            attachment_name: attachment.attachment_name || '',
            file_type: attachment.file_type || attachmentTypes[0],
            file: null,
            expiration_date: attachment.expiration_date || '',
        });
        setShowAttachmentForm(true);
    };

    const deleteAttachment = async (attachment) => {
        const result = await Swal.fire({
            title: t('interpreter.deleteAttachmentQuestion'),
            text: t('interpreter.deleteAttachmentText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.delete'),
        });
        if (!result.isConfirmed) return;

        const response = await Api.deleteInterpreterAttachment(attachment.id);
        if (response?.status >= 200 && response?.status < 300) {
            toast.success(response.data?.message || t('interpreter.attachmentDeleted'), { className: 'successToast' });
            await refreshAttachments();
        } else {
            toast.error(response?.data?.message || t('interpreter.attachmentDeleteFailed'), { className: 'errorToast' });
        }
    };

    const isExpired = (date) => date && new Date(`${date}T00:00:00`) < new Date(new Date().toDateString());
    const canManage = canManageDocuments();

    const deferredItems = pendingAttachments.map((attachment, index) => ({ ...attachment, _pendingIndex: index }));

    const saveDeferredAttachment = () => {
        const newErrors = {};
        if (!pendingAttachmentForm?.attachment_name?.trim()) {
            newErrors.attachment_name = t('interpreter.attachmentNameRequired');
        }
        if (!pendingAttachmentForm?.file_type) {
            newErrors.file_type = t('interpreter.fileTypeRequired');
        }
        if (!editingAttachment && !pendingAttachmentForm?.file) {
            newErrors.file = t('interpreter.fileRequired');
        }

        if (Object.keys(newErrors).length > 0) {
            setDeferredAttachmentErrors(newErrors);
            return;
        }
        setDeferredAttachmentErrors({});

        if (editingAttachment?._pendingIndex !== undefined) {
            update(editingAttachment._pendingIndex, pendingAttachmentForm);
        } else {
            append(pendingAttachmentForm);
        }

        setPendingAttachmentForm(null);
        setFileTypeState(null);
        setEditingAttachment(null);
        setShowAttachmentForm(false);
    };

    const resetDeferredAttachmentForm = () => {
        setDeferredAttachmentErrors({});
        setPendingAttachmentForm(null);
        setFileTypeState(null);
        setEditingAttachment(null);
        setShowAttachmentForm(false);
    };

    return (
        <div className="row-primary flex-column">
            <h2 className="fst-italic fw-bold mt-0">{t('interpreter.vendorAgreement')}</h2>
            {canManage ? (
                <div>
                <div>
                    <div className="row-primary" style={{ alignItems: 'baseline' }}>
                        <h2>{t('interpreter.agreementFile')}</h2>
                        <Controller
                            name="vendor_agreement_file"
                            control={deferredAgreementControl}
                            render={({ field }) => (
                                <div className="w-100">
                                    <input className="input-in type_inp" style={{ paddingTop: '10px' }} type="file" onChange={(event) => field.onChange(event.target.files?.[0] || null)} />
                                    {item?.vendor_agreement_file && (
                                        <div className='mt-2' style={{ marginBottom: 8 }}>
                                            <a href={documentUrl(item.vendor_agreement_file)} target="_blank" rel="noreferrer noopener">
                                               <i className="fas fa-file-alt me-1"></i> {item.vendor_agreement_file.split('/').pop()}
                                            </a>
                                        </div>
                                    )}
                                    {deferredAgreementErrors?.vendor_agreement_file?.message && <ErrorMessage text={deferredAgreementErrors.vendor_agreement_file.message} />}
                                </div>
                            )}
                        />
                    </div>
                    <div className="row-primary">
                        <h2>{t('interpreter.expiryDate')}</h2>
                        <Controller
                            name="vendor_agreement_expiry"
                            control={deferredAgreementControl}
                            render={({ field }) => (
                                <div className="w-100">
                                    <div className="input-image">
                                        <input type="date" {...field} />
                                        <img alt="celender Icon" className="custom-icon" src="/images/celender.png" />
                                    </div>
                                    {deferredAgreementErrors?.vendor_agreement_expiry?.message && <ErrorMessage text={deferredAgreementErrors.vendor_agreement_expiry.message} />}
                                </div>
                            )}
                        />
                    </div>
                </div>
                </div>
            ) : (
                <div>
                    {item?.vendor_agreement_file && <a href={documentUrl(item.vendor_agreement_file)} target="_blank" rel="noreferrer noopener">{t('interpreter.viewVendorAgreement')}</a>}
                    <div>{item?.vendor_agreement_expiry || t('interpreter.noExpiryDate')}</div>
                </div>
            )}

            <h2 className="fst-italic fw-bold mt-0">{t('interpreter.attachments')}</h2>
            <div style={{ overflowX: 'auto' }}>
                <table>
                    <thead>
                        <tr><th>{t('interpreter.attachmentName')}</th><th>{t('interpreter.fileType')}</th><th>{t('interpreter.file')}</th><th>{t('interpreter.expirationDate')}</th>{canManage && <th>{t('interpreter.actions')}</th>}</tr>
                    </thead>
                    <tbody>
                        {attachments.length === 0 && pendingAttachments.length === 0 && <tr><td colSpan={canManage ? '5' : '4'}>{t('interpreter.noAttachmentsUploaded')}</td></tr>}
                        {[...attachments, ...(isDeferred ? deferredItems : [])].map((attachment) => (
                            <tr key={attachment.id}>
                                <td>{attachment.attachment_name}</td>
                                <td>{attachment.file_type}</td>
                                <td>{attachment.file_path ? <a href={documentUrl(attachment.file_path)} target="_blank" rel="noreferrer noopener">{attachment.original_file_name || t('interpreter.viewFile')}</a> : attachment.file?.name || t('interpreter.pendingUpload')}</td>
                                <td>{attachment.expiration_date || t('common.nA')} {isExpired(attachment.expiration_date) && <span style={{ color: '#c62828', fontWeight: 600 }}>{t('interpreter.expired')}</span>}</td>
                                {canManage && <td>
                                    {(!isDeferred || attachment._pendingIndex !== undefined) && <button type="button" className="table_edit" onClick={() => editAttachment(attachment)}>{t('interpreter.edit')}</button>}{' '}
                                    <button type="button" className="table_delete" onClick={() => attachment._pendingIndex !== undefined ? remove(attachment._pendingIndex) : deleteAttachment(attachment)}>{t('common.delete')}</button>
                                </td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {canManage && <>
                <button type="button" className="main_button" onClick={() => { if (isDeferred) { setDeferredAttachmentErrors({}); setFileTypeState({ label: attachmentTypes[0], value: attachmentTypes[0] }); setPendingAttachmentForm({ attachment_name: '', file_type: attachmentTypes[0], file: null, expiration_date: '' }); } else { resetAttachmentForm(); } setShowAttachmentForm(true); }}>{t('interpreter.addAttachment')}</button>

                {showAttachmentForm && (
                    isDeferred ? (
                    <div className="profile_form" style={{ marginTop: 20 }}>
                        <h3 className="fst-italic fw-bold mt-0">{editingAttachment ? t('interpreter.editAttachment') : t('interpreter.addAttachment')}</h3>
                        
                        <div className="row-primary">
                            <div className="row-primary w-100">
                                <h2>{t('interpreter.attachmentName')} *</h2>
                                <Controller
                                    name="attachment_name"
                                    control={attachmentControl}
                                    rules={{ required: t('interpreter.attachmentNameRequired') }}
                                    render={({ field }) => (
                                        <div className="w-100">
                                            <input className="input-in type_inp" type="text" placeholder={t('interpreter.attachmentNamePlaceholder')} value={pendingAttachmentForm?.attachment_name || ''} onChange={(event) => setPendingAttachmentForm({ ...pendingAttachmentForm, attachment_name: event.target.value })} />
                                            {deferredAttachmentErrors.attachment_name && <ErrorMessage text={deferredAttachmentErrors.attachment_name} />}
                                        </div>
                                    )}
                                />
                            </div>
                            <div className="row-primary w-100">
                                <h2>{t('interpreter.fileType')} *</h2>
                                <Controller
                                    name="file_type"
                                    control={attachmentControl}
                                    rules={{ required: t('interpreter.fileTypeRequired') }}
                                    render={({ field }) => (
                                        <div className="w-100">
                                            <AsyncPaginate
                                                menuPlacement="auto"
                                                styles={selectCustomStyles}
                                                value={fileTypeState}
                                                loadOptions={fileTypeLoadOptions}
                                                isClearable={false}
                                                onChange={(selected) => {
                                                    const fileType = selected?.value || attachmentTypes[0];
                                                    setFileTypeState(selected || { label: fileType, value: fileType });
                                                    setPendingAttachmentForm({ ...pendingAttachmentForm, file_type: fileType });
                                                }}
                                                additional={{ page: 1 }}
                                            />
                                            {deferredAttachmentErrors.file_type && <ErrorMessage text={deferredAttachmentErrors.file_type} />}
                                        </div>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="row-primary">
                            <div className="row-primary w-100">
                                <h2>{t('interpreter.file')} {!editingAttachment && '*'}</h2>
                                <Controller
                                    name="file"
                                    control={attachmentControl}
                                    rules={{ required: !editingAttachment ? t('interpreter.fileRequired') : false }}
                                    render={({ field }) => (
                                        <div className="w-100">
                                            <input className="input-in type_inp" style={{ paddingTop: '10px' }} type="file" onChange={(event) => setPendingAttachmentForm({ ...pendingAttachmentForm, file: event.target.files?.[0] || null })} />
                                            {deferredAttachmentErrors.file && <ErrorMessage text={deferredAttachmentErrors.file} />}
                                        </div>
                                    )}
                                />
                            </div>
                            <div className="row-primary w-100">
                                <h2>{t('interpreter.expirationDate')}</h2>
                                <Controller
                                    name="expiration_date"
                                    control={attachmentControl}
                                    render={({ field }) => (
                                        <div className="w-100">
                                            <div className="input-image">
                                            <input type="date" value={pendingAttachmentForm?.expiration_date || ''} onChange={(event) => setPendingAttachmentForm({ ...pendingAttachmentForm, expiration_date: event.target.value })} />
                                            <img alt="celender Icon" className="custom-icon" src="/images/celender.png" />
                                            {attachmentErrors?.expiration_date?.message && <ErrorMessage text={attachmentErrors.expiration_date.message} />}
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>

                        <button type="button" className="main_button" onClick={saveDeferredAttachment}>{editingAttachment ? t('interpreter.updateAttachment') : t('interpreter.saveAttachment')}</button>{' '}
                        <button type="button" className="Cancle_button" onClick={resetDeferredAttachmentForm}>{t('common.cancel')}</button>
                    </div>
                    ) : (
                    <form onSubmit={handleAttachmentFormSubmit(handleAttachmentSubmit)} className="profile_form" style={{ marginTop: 20 }}>
                        <h3 className="fst-italic fw-bold mt-0">{editingAttachment ? t('interpreter.editAttachment') : t('interpreter.addAttachment')}</h3>
                        <Controller name="attachment_name" control={attachmentControl} rules={{ required: t('interpreter.attachmentNameRequired') }} render={({ field }) => <div className="row-primary"><h2>{t('interpreter.attachmentName')} *</h2><div className="w-100"><input className="input-in type_inp" type="text" {...field} />{attachmentErrors?.attachment_name?.message && <ErrorMessage text={attachmentErrors.attachment_name.message} />}</div></div>} />
                        <Controller name="file_type" control={attachmentControl} rules={{ required: t('interpreter.fileTypeRequired') }} render={({ field }) => <div className="row-primary"><h2>{t('interpreter.fileType')} *</h2><div className="w-100"><AsyncPaginate menuPlacement="auto" styles={selectCustomStyles} value={field.value ? { label: field.value, value: field.value } : null} loadOptions={fileTypeLoadOptions} isClearable={false} onChange={(selected) => field.onChange(selected?.value || attachmentTypes[0])} additional={{ page: 1 }} />{attachmentErrors?.file_type?.message && <ErrorMessage text={attachmentErrors.file_type.message} />}</div></div>} />
                        <Controller name="file" control={attachmentControl} rules={{ required: !editingAttachment ? t('interpreter.fileRequired') : false }} render={({ field }) => <div className="row-primary"><h2>{t('interpreter.file')} {!editingAttachment && '*'}</h2><div className="w-100"><input className="input-in type_inp" type="file" onChange={(event) => field.onChange(event.target.files?.[0] || null)} />{attachmentErrors?.file?.message && <ErrorMessage text={attachmentErrors.file.message} />}</div></div>} />
                        <Controller name="expiration_date" control={attachmentControl} render={({ field }) => <div className="row-primary"><h2>{t('interpreter.expirationDate')}</h2><div className="w-100"><input className="input-in type_inp" type="date" {...field} /></div></div>} />
                        <button type="submit" className="main_button">{editingAttachment ? t('interpreter.updateAttachment') : t('interpreter.saveAttachment')}</button>{' '}<button type="button" className="Cancle_button" onClick={resetAttachmentForm}>{t('common.cancel')}</button>
                    </form>
                    ))}
            </>}
        </div>
    );
};

export default VerificationDocuments;
